import { useInfiniteQuery } from '@tanstack/react-query'
import { fetchApiWithToken } from '~/api/utils'
import type { ConversationEntityType, ConversationsResponse } from '../types'

export const useConversations = ({
  search,
  entityType,
  includeCampaigns = false,
  unreadOnly = false
}: {
  search?: string
  entityType?: ConversationEntityType | 'all'
  includeCampaigns?: boolean
  unreadOnly?: boolean
}) => {
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError
  } = useInfiniteQuery<ConversationsResponse, Error>({
    queryKey: [
      'messaging',
      'conversations',
      search,
      entityType,
      includeCampaigns,
      unreadOnly
    ],
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = allPages.length
      if (lastPage['hydra:totalItems'] > currentPage * 30) {
        return currentPage + 1
      }
      return undefined
    },
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams()
      params.append('page', String(pageParam))
      if (search) params.append('search', search)
      if (entityType && entityType !== 'all')
        params.append('entityType', entityType)
      if (includeCampaigns) params.append('includeCampaigns', 'true')
      if (unreadOnly) params.append('unreadOnly', 'true')

      return fetchApiWithToken<ConversationsResponse>({
        method: 'get',
        url: `/v1/messaging/conversations?${params.toString()}`,
        useLDJson: true
      })
    },
    staleTime: 60_000
  })

  return {
    items: data?.pages.flatMap((page) => page['hydra:member']),
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError,
    totalItems: data?.pages[0]?.['hydra:totalItems'] ?? 0
  }
}
